import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../api/api';
import './SelectableInput.css';

export default function SelectableInput({
  value = '',
  onChange = () => {},
  onBlur = () => {},
  className = '',
  placeholder = '',
  tableName = '',
  columnName = '',
  disabled = false,
  ...props
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load options from API
  const loadOptions = async () => {
    if (!tableName || !columnName || loading) return;
    
    setLoading(true);
    try {
      console.log('Loading options for column:', columnName, 'from table:', tableName);
      const data = await apiFetch(`/api/distinct/${tableName}/${columnName}`);
      const optionsList = Array.isArray(data) ? data : (data.data || []);
      setOptions(optionsList);
      setFilteredOptions(optionsList);
    } catch (err) {
      console.error('Failed to load options:', err);
      setOptions([]);
      setFilteredOptions([]);
    }
    setLoading(false);
  };

  // Reset options when tableName or columnName changes
  useEffect(() => {
    setOptions([]);
    setFilteredOptions([]);
    setShowDropdown(false);
  }, [tableName, columnName]);

  // Filter options based on current input value
  useEffect(() => {
    if (!value || value.length === 0) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => 
        option.toString().toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [value, options]);

  // Handle input focus
  const handleFocus = () => {
    if (options.length === 0 && tableName && columnName) {
      loadOptions();
    }
    if (tableName && columnName) {
      setShowDropdown(true);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Auto-load options when user starts typing if not loaded yet
    if (newValue.length > 0 && options.length === 0 && tableName && columnName) {
      loadOptions();
    }
    
    // Show dropdown when typing if this field has auto-complete capability
    if (tableName && columnName) {
      setShowDropdown(true);
    }
  };

  // Handle option selection
  const handleOptionSelect = (option) => {
    onChange(option);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  // Handle input blur with delay to allow option selection
  const handleBlur = (e) => {
    // Delay hiding dropdown to allow option click
    setTimeout(() => {
      setShowDropdown(false);
      onBlur(e);
    }, 150);
  };



  // Handle clear button
  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div className={`selectable-input-wrapper`} ref={dropdownRef}>
      
        <input
          {...props}
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="selectable-input"
        />
        
        
        {loading && tableName && columnName && (
          <div className="loading-indicator">⏳</div>
        )}

      
      {showDropdown && filteredOptions.length > 0 && (
        <div className="selectable-dropdown">
          {filteredOptions.slice(0, 10).map((option, index) => (
            <div
              key={index}
              className="dropdown-option"
              onClick={() => handleOptionSelect(option)}
            >
              {option}
            </div>
          ))}
          {filteredOptions.length > 10 && (
            <div className="dropdown-more">
              ... and {filteredOptions.length - 10} more
            </div>
          )}
        </div>
      )}
      
      {showDropdown && filteredOptions.length === 0 && options.length > 0 && (
        <div className="selectable-dropdown">
          <div className="dropdown-no-match">No matching options</div>
        </div>
      )}
    </div>
  );
} 